import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Label,
    FormGroup,
    Input,
    Row,
    Col,
    Alert,
} from 'reactstrap';
import moment from 'moment';
import { Rates } from '../../../services';
import { copyRates, copyRatesSequence } from '../../../redux/modules/rates';

const tripletKey = (r) => `${r.payment_system_id}|${r.acquirer_id}|${r.card_type_id}`;

const CopyRatesModal = ({ isOpen, toggle, accountId }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { accounts } = useSelector((state) => state.accounts);
    const { payment_systems } = useSelector((state) => state.payment_systems);
    const { acquirers } = useSelector((state) => state.acquirers);
    const { card_types } = useSelector((state) => state.card_types);
    const { rates: targetRates } = useSelector((state) => state.rates);

    const [sourceId, setSourceId] = useState('');
    const [sourceRates, setSourceRates] = useState([]);
    const [loadingSource, setLoadingSource] = useState(false);
    const [startDate, setStartDate] = useState(() => moment().format('YYYY-MM-DD'));
    const [selectedPs, setSelectedPs] = useState({});
    const [selectedAq, setSelectedAq] = useState({});
    const [selectedCt, setSelectedCt] = useState({});

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        if (!sourceId) {
            setSourceRates([]);
            return;
        }
        let cancelled = false;
        setLoadingSource(true);
        Rates.list({ account_id: sourceId })
            .then((data) => {
                if (!cancelled) {
                    setSourceRates(data.rates || []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setSourceRates([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingSource(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen, sourceId]);

    useEffect(() => {
        if (!sourceRates.length) {
            setSelectedPs({});
            setSelectedAq({});
            setSelectedCt({});
            return;
        }
        const ps = {};
        const aq = {};
        const ct = {};
        sourceRates.forEach((r) => {
            ps[r.payment_system_id] = true;
            aq[r.acquirer_id] = true;
            ct[r.card_type_id] = true;
        });
        setSelectedPs(ps);
        setSelectedAq(aq);
        setSelectedCt(ct);
    }, [sourceRates]);

    const targetKeySet = useMemo(() => {
        const set = new Set();
        (targetRates || []).forEach((r) => set.add(tripletKey(r)));
        return set;
    }, [targetRates]);

    const missingTripletKeys = useMemo(() => {
        const seen = new Set();
        const missing = [];
        sourceRates.forEach((r) => {
            const k = tripletKey(r);
            if (seen.has(k)) {
                return;
            }
            seen.add(k);
            if (!targetKeySet.has(k)) {
                missing.push(k);
            }
        });
        return missing;
    }, [sourceRates, targetKeySet]);

    const nameLookup = useMemo(() => {
        const ps = Object.fromEntries((payment_systems || []).map((x) => [x.id, x.name]));
        const aq = Object.fromEntries((acquirers || []).map((x) => [x.id, x.name]));
        const ct = Object.fromEntries((card_types || []).map((x) => [x.id, x.name]));
        return { ps, aq, ct };
    }, [payment_systems, acquirers, card_types]);

    const toggleMap = (setter, id) => {
        setter((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleColumnSelectAll = (setter, ids, selectedMap) => {
        const allSelected = ids.length > 0 && ids.every((id) => selectedMap[id]);
        const newVal = !allSelected;
        setter((prev) => {
            const next = { ...prev };
            ids.forEach((id) => {
                next[id] = newVal;
            });
            return next;
        });
    };

    const psMasterRef = useRef(null);
    const aqMasterRef = useRef(null);
    const ctMasterRef = useRef(null);

    const idsInSource = (dim) => {
        const s = new Set();
        sourceRates.forEach((r) => {
            if (dim === 'ps') {
                s.add(r.payment_system_id);
            } else if (dim === 'aq') {
                s.add(r.acquirer_id);
            } else {
                s.add(r.card_type_id);
            }
        });
        return [...s];
    };

    const buildPayloadBase = () => ({
        source_id: sourceId,
        target_id: accountId,
        start_at: moment(startDate).startOf('day').toISOString(),
    });

    const validateSelection = () => {
        const ps = Object.keys(selectedPs).filter((k) => selectedPs[k]);
        const aq = Object.keys(selectedAq).filter((k) => selectedAq[k]);
        const ct = Object.keys(selectedCt).filter((k) => selectedCt[k]);
        return { ps, aq, ct };
    };

    const handleSubmit = () => {
        const { ps, aq, ct } = validateSelection();
        if (!sourceId || !ps.length || !aq.length || !ct.length) {
            return;
        }
        dispatch(
            copyRates({
                ...buildPayloadBase(),
                payment_system_ids: ps,
                acquirer_ids: aq,
                card_type_ids: ct,
            })
        )
            .then(() => toggle())
            .catch(() => {});
    };

    const handleCopyMissingOnly = () => {
        if (!sourceId || !missingTripletKeys.length) {
            return;
        }
        const base = buildPayloadBase();
        const payloads = missingTripletKeys.map((key) => {
            const [ps, aq, ct] = key.split('|');
            return {
                ...base,
                payment_system_ids: [ps],
                acquirer_ids: [aq],
                card_type_ids: [ct],
            };
        });
        dispatch(copyRatesSequence(payloads))
            .then(() => toggle())
            .catch(() => {});
    };

    const psOptions = idsInSource('ps');
    const aqOptions = idsInSource('aq');
    const ctOptions = idsInSource('ct');

    const psAllSelected = psOptions.length > 0 && psOptions.every((id) => selectedPs[id]);
    const psSomeSelected = psOptions.some((id) => selectedPs[id]);
    const psIndeterminate = psSomeSelected && !psAllSelected;

    const aqAllSelected = aqOptions.length > 0 && aqOptions.every((id) => selectedAq[id]);
    const aqSomeSelected = aqOptions.some((id) => selectedAq[id]);
    const aqIndeterminate = aqSomeSelected && !aqAllSelected;

    const ctAllSelected = ctOptions.length > 0 && ctOptions.every((id) => selectedCt[id]);
    const ctSomeSelected = ctOptions.some((id) => selectedCt[id]);
    const ctIndeterminate = ctSomeSelected && !ctAllSelected;

    useEffect(() => {
        if (psMasterRef.current) {
            psMasterRef.current.indeterminate = psIndeterminate;
        }
    }, [psIndeterminate, psOptions.length]);

    useEffect(() => {
        if (aqMasterRef.current) {
            aqMasterRef.current.indeterminate = aqIndeterminate;
        }
    }, [aqIndeterminate, aqOptions.length]);

    useEffect(() => {
        if (ctMasterRef.current) {
            ctMasterRef.current.indeterminate = ctIndeterminate;
        }
    }, [ctIndeterminate, ctOptions.length]);

    const selectionValid = () => {
        const { ps, aq, ct } = validateSelection();
        return sourceId && ps.length > 0 && aq.length > 0 && ct.length > 0;
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" contentClassName="rates-modal-content">
            <ModalHeader toggle={toggle}>{t('rates.copy_modal_title')}</ModalHeader>
            <ModalBody>
                <FormGroup>
                    <Label>{t('rates.copy_source_account')}</Label>
                    <Input
                        type="select"
                        value={sourceId}
                        onChange={(e) => setSourceId(e.target.value)}
                    >
                        <option value="">{t('rates.copy_select_source')}</option>
                        {accounts &&
                            accounts
                                .filter((a) => a.id !== accountId)
                                .map((a) => (
                                    <option key={a.id} value={a.id}>
                                        {a.name}
                                    </option>
                                ))}
                    </Input>
                </FormGroup>
                <FormGroup>
                    <Label>{t('rates.copy_start_date')}</Label>
                    <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </FormGroup>
                {loadingSource && sourceId && (
                    <Alert color="light">{t('rates.copy_loading_source')}</Alert>
                )}
                {sourceId && !loadingSource && (
                    <>
                        {missingTripletKeys.length > 0 && (
                            <Alert color="info" className="mb-3">
                                <div className="mb-2">
                                    {t('rates.copy_missing_triplets', {
                                        count: missingTripletKeys.length,
                                    })}
                                </div>
                                <Button
                                    color="info"
                                    size="sm"
                                    onClick={handleCopyMissingOnly}
                                    disabled={!sourceId || missingTripletKeys.length === 0}
                                >
                                    {t('rates.copy_missing_only')}
                                </Button>
                            </Alert>
                        )}
                        <Row>
                            <Col md={4}>
                                <Label>{t('rates.copy_filter_psp')}</Label>
                                {psOptions.length > 0 && (
                                    <FormGroup check className="mb-2 pb-2 border-bottom">
                                        <Label check>
                                            <Input
                                                innerRef={psMasterRef}
                                                type="checkbox"
                                                checked={psAllSelected}
                                                onChange={() =>
                                                    toggleColumnSelectAll(setSelectedPs, psOptions, selectedPs)
                                                }
                                            />{' '}
                                            <strong>{t('rates.copy_column_toggle_all')}</strong>
                                        </Label>
                                    </FormGroup>
                                )}
                                {psOptions.map((id) => (
                                    <FormGroup check key={id}>
                                        <Label check>
                                            <Input
                                                type="checkbox"
                                                checked={!!selectedPs[id]}
                                                onChange={() => toggleMap(setSelectedPs, id)}
                                            />{' '}
                                            {nameLookup.ps[id] || id}
                                        </Label>
                                    </FormGroup>
                                ))}
                            </Col>
                            <Col md={4}>
                                <Label>{t('rates.copy_filter_acquirer')}</Label>
                                {aqOptions.length > 0 && (
                                    <FormGroup check className="mb-2 pb-2 border-bottom">
                                        <Label check>
                                            <Input
                                                innerRef={aqMasterRef}
                                                type="checkbox"
                                                checked={aqAllSelected}
                                                onChange={() =>
                                                    toggleColumnSelectAll(setSelectedAq, aqOptions, selectedAq)
                                                }
                                            />{' '}
                                            <strong>{t('rates.copy_column_toggle_all')}</strong>
                                        </Label>
                                    </FormGroup>
                                )}
                                {aqOptions.map((id) => (
                                    <FormGroup check key={id}>
                                        <Label check>
                                            <Input
                                                type="checkbox"
                                                checked={!!selectedAq[id]}
                                                onChange={() => toggleMap(setSelectedAq, id)}
                                            />{' '}
                                            {nameLookup.aq[id] || id}
                                        </Label>
                                    </FormGroup>
                                ))}
                            </Col>
                            <Col md={4}>
                                <Label>{t('rates.copy_filter_card_type')}</Label>
                                {ctOptions.length > 0 && (
                                    <FormGroup check className="mb-2 pb-2 border-bottom">
                                        <Label check>
                                            <Input
                                                innerRef={ctMasterRef}
                                                type="checkbox"
                                                checked={ctAllSelected}
                                                onChange={() =>
                                                    toggleColumnSelectAll(setSelectedCt, ctOptions, selectedCt)
                                                }
                                            />{' '}
                                            <strong>{t('rates.copy_column_toggle_all')}</strong>
                                        </Label>
                                    </FormGroup>
                                )}
                                {ctOptions.map((id) => (
                                    <FormGroup check key={id}>
                                        <Label check>
                                            <Input
                                                type="checkbox"
                                                checked={!!selectedCt[id]}
                                                onChange={() => toggleMap(setSelectedCt, id)}
                                            />{' '}
                                            {nameLookup.ct[id] || id}
                                        </Label>
                                    </FormGroup>
                                ))}
                            </Col>
                        </Row>
                        {missingTripletKeys.length > 0 && (
                            <div className="mt-3 small text-muted">
                                <div>{t('rates.copy_missing_list_title')}</div>
                                <ul className="mb-0 pl-3">
                                    {missingTripletKeys.slice(0, 12).map((key) => {
                                        const [ps, aq, ct] = key.split('|');
                                        return (
                                            <li key={key}>
                                                {nameLookup.ps[ps] || ps} /{' '}
                                                {nameLookup.aq[aq] || aq} /{' '}
                                                {nameLookup.ct[ct] || ct}
                                            </li>
                                        );
                                    })}
                                </ul>
                                {missingTripletKeys.length > 12 && (
                                    <div>… ({missingTripletKeys.length - 12} {t('rates.copy_more')})</div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    {t('common.close')}
                </Button>
                <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!selectionValid() || loadingSource}
                >
                    {t('rates.copy_confirm')}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CopyRatesModal;
